import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Portfolio images for homepage - Réalisations récentes
const portfolioImages = [
  { id: "1", image_url: "https://i.ibb.co/0VmH7NPs/IMG-8211.avif" },
  { id: "2", image_url: "https://i.ibb.co/jkRvNLMn/IMG-7093.jpg" },
  { id: "3", image_url: "https://i.ibb.co/sp6xTVwP/IMG-8180.avif" },
  { id: "4", image_url: "https://i.ibb.co/4nbZtTNz/IMG-7890.avif" },
  { id: "5", image_url: "https://i.ibb.co/HTdVc6Dr/IMG-6858.avif" },
  { id: "6", image_url: "https://i.ibb.co/62pjg6Q/IMG-7433.avif" },
];

const Home = () => {
  useEffect(() => {
    const init = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        console.error(e);
      }
    };
    init();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1 animate-fade-in-up">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
                Tatoueuse — Les Sables-d'Olonne
              </p>
              <h1 className="text-5xl md:text-7xl font-heading font-light tracking-tight mb-6">
                De l'idée à la peau :<br />
                <span className="italic">l'art prend vie.</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground font-light max-w-md mb-10 leading-relaxed">
                Créations personnalisées dans un univers doux et artistique. Chaque tatouage est une œuvre unique, pensée avec vous.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/demande-projet" data-testid="hero-cta-project">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs w-full sm:w-auto">
                    Demande de projet
                    <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </Link>
                <Link to="/rendez-vous" data-testid="hero-cta-rdv">
                  <Button variant="outline" className="h-12 px-8 uppercase tracking-widest text-xs w-full sm:w-auto border-foreground text-foreground hover:bg-foreground hover:text-background">
                    Prendre rendez-vous
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="order-1 lg:order-2 animate-fade-in animation-delay-200">
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src="https://i.ibb.co/rKMvNHBN/08153e28-f2d2-469b-a5cf-5413b0e24536.jpg"
                    alt="NYMPHEA - Tatoueuse"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-background p-6 border border-border/40">
                  <p className="font-heading text-xl italic">@nymphea.ttt</p>
                  <a
                    href="https://instagram.com/nymphea.ttt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground mt-2 hover:text-foreground transition-colors"
                    data-testid="hero-instagram"
                  >
                    <Instagram className="w-4 h-4" strokeWidth={1.5} />
                    Suivre sur Instagram
                  </a>
                  <a
                    href="https://www.tiktok.com/@nymphea.ttt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground mt-2 hover:text-foreground transition-colors"
                    data-testid="hero-tiktok"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                    Suivre sur TikTok
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-24 md:py-32 border-t border-border/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                Portfolio
              </p>
              <h2 className="text-3xl md:text-5xl font-heading font-light tracking-tight">
                Réalisations récentes
              </h2>
            </div>
            <Link
              to="/portfolio"
              className="text-sm uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors link-underline"
              data-testid="portfolio-link"
            >
              Voir tout
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {portfolioImages.map((item, index) => (
              <div
                key={item.id}
                className={`img-hover-zoom overflow-hidden ${
                  index === 0 ? "md:col-span-2 md:row-span-2" : ""
                }`}
              >
                <img
                  src={item.image_url}
                  alt="Tatouage"
                  className="w-full h-full object-cover aspect-square"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div className="img-hover-zoom overflow-hidden">
              <img
                src="https://i.ibb.co/3yFV4nDT/IMG-6193.jpg"
                alt="NYMPHEA au travail"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                À propos
              </p>
              <h2 className="text-3xl md:text-5xl font-heading font-light tracking-tight mb-6">
                Une approche<br />
                <span className="italic">personnalisée</span>
              </h2>
              <p className="text-base text-muted-foreground font-light leading-relaxed mb-6">
                Installée à l'Atelier La Peauésie aux Sables-d'Olonne, je crée des tatouages uniques en m'inspirant de votre histoire et de vos envies. Mon approche se veut douce, à l'écoute, pour que chaque séance soit un moment agréable.
              </p>
              <p className="text-base text-muted-foreground font-light leading-relaxed mb-8">
                Spécialisée dans le fine line, le micro-réalisme et le semi-réalisme, je m'inspire de la nature, des émotions humaines et de la nostalgie pour créer des pièces uniques et intemporelles.
              </p>
              <Link to="/a-propos" data-testid="about-link">
                <Button variant="outline" className="h-12 px-8 uppercase tracking-widest text-xs border-foreground text-foreground hover:bg-foreground hover:text-background">
                  En savoir plus
                  <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Flashs Preview */}
      <section className="py-24 md:py-32 border-t border-border/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Flashs disponibles
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-light tracking-tight mb-4">
              Designs prêts à tatouer
            </h2>
            <p className="text-base text-muted-foreground font-light max-w-lg mx-auto">
              Découvrez ma collection de flashs uniques, disponibles à la réservation.
            </p>
          </div>
          <div className="flex justify-center">
            <Link to="/flashs" data-testid="flashs-link">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs">
                Découvrir les flashs
                <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-light tracking-tight mb-6">
            Prêt·e à vous lancer ?
          </h2>
          <p className="text-base font-light opacity-80 max-w-lg mx-auto mb-10">
            Que vous ayez une idée précise ou simplement une envie, je suis là pour vous accompagner dans votre projet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demande-projet" data-testid="cta-final-project">
              <Button className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 h-12 px-8 uppercase tracking-widest text-xs">
                Soumettre un projet
              </Button>
            </Link>
            <Link to="/contact" data-testid="cta-final-contact">
              <Button variant="outline" className="border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 h-12 px-8 uppercase tracking-widest text-xs">
                Me contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
